/**
 * Built-in Smart Block catalog — ready-to-use kinds that appear in the picker
 * without anyone having to author them.
 *
 * **Scope (2026-05-12 decision):** Smart Blocks are SPEC BODY CONTENT only —
 * dynamic prose / lists / tables that live INSIDE a section's paragraphs.
 * Anything that lives on the title page, in headers/footers, or in the page
 * chrome (project name, CSI number + title, issue date, engineer of record
 * block) is FORMA territory — handled by the existing forma token system,
 * not by Smart Blocks. Don't add built-ins for that — formas already do it.
 *
 * These are synthetic SmartBlock records (not stored in the DB) — the picker
 * merges this list with the firm's published custom smart blocks. When an
 * author inserts one, the resulting ContentBlock just carries
 * `smartBlockKind` + `smartParams`, with `smartBlockId` left blank.
 * Resolvers handle the "no smartBlock row" case by falling back to defaults.
 */

import type { SmartBlock, SmartBlockKind } from '../../shared/types';

const BUILT_IN_ID_PREFIX = '__builtin__';

export function isBuiltInSmartBlockId(id: string | undefined): boolean {
  return !!id && id.startsWith(BUILT_IN_ID_PREFIX);
}

export function builtInIdFor(kind: SmartBlockKind): string {
  return `${BUILT_IN_ID_PREFIX}${kind}`;
}

export const BUILT_IN_SMART_BLOCKS: SmartBlock[] = [
  {
    id: builtInIdFor('related-sections'),
    name: 'Related Sections',
    description:
      'Auto-list every other spec section in the project. Supports division filter and exclude list.',
    kind: 'related-sections',
    icon: 'Link2',
    color: '#228be6',
    category: 'Cross-reference',
    paramSchema: [
      {
        key: 'header',
        label: 'Header line',
        type: 'string',
        default: 'Related Sections',
        placeholder: 'e.g. Related Sections',
      },
      {
        key: 'divisionFilter',
        label: 'Division filter',
        type: 'division',
        description: 'Limit to specs in this CSI division (leave blank for all)',
        placeholder: 'e.g. 27',
      },
      {
        key: 'sortBy',
        label: 'Sort by',
        type: 'enum',
        default: 'csiNumber',
        options: [
          { value: 'csiNumber', label: 'CSI Number' },
          { value: 'title', label: 'Title' },
        ],
      },
      {
        key: 'format',
        label: 'Format',
        type: 'enum',
        default: 'csi-and-title',
        options: [
          { value: 'csi-and-title', label: 'CSI # — Title' },
          { value: 'csi-only', label: 'CSI # only' },
          { value: 'title-only', label: 'Title only' },
        ],
      },
      {
        key: 'excludeIds',
        label: 'Exclude specs',
        type: 'spec-ref-list',
        description: 'Specs to skip even if they match the filter',
      },
      {
        key: 'includeOwn',
        label: 'Include this spec',
        type: 'boolean',
        default: false,
      },
    ],
    defaultParams: {
      header: 'Related Sections',
      sortBy: 'csiNumber',
      format: 'csi-and-title',
      includeOwn: false,
    },
    version: 1,
    status: 'published',
    lastModified: '',
  },
  // ─── Forma territory (NOT smart blocks) — kept here as a comment so future
  //     PRs don't accidentally add them back. These are header/footer/title-
  //     page metadata that the forma token system already handles:
  //       - section-header       (CSI # + title)
  //       - project-identity     (project name / number / client / address)
  //       - issue-date           (current issuance date)
  //       - engineer-of-record   (title-block author/PIC of record)
  //     Resolvers for these kinds are kept in resolvers.ts so any existing
  //     instances saved by users still resolve correctly until they're
  //     migrated; new instances should not be authored via Smart Blocks.
  {
    id: builtInIdFor('standards-referenced'),
    name: 'Standards Referenced',
    description:
      "Scans this section's content for ASTM/ANSI/UL/NEMA/IEEE/NFPA citations and lists them.",
    kind: 'standards-referenced',
    icon: 'BookOpen',
    color: '#e8590c',
    category: 'Cross-reference',
    paramSchema: [
      {
        key: 'header',
        label: 'Header line',
        type: 'string',
        default: 'Reference Standards:',
      },
      {
        key: 'organizations',
        label: 'Organizations to scan for',
        type: 'multiselect',
        default: ['ASTM', 'ANSI', 'UL', 'NEMA', 'IEEE', 'NFPA', 'IEC', 'ISO'],
        options: [
          { value: 'ASTM', label: 'ASTM' },
          { value: 'ANSI', label: 'ANSI' },
          { value: 'UL', label: 'UL' },
          { value: 'NEMA', label: 'NEMA' },
          { value: 'IEEE', label: 'IEEE' },
          { value: 'NFPA', label: 'NFPA' },
          { value: 'IEC', label: 'IEC' },
          { value: 'ISO', label: 'ISO' },
        ],
      },
    ],
    defaultParams: {
      header: 'Reference Standards:',
      organizations: ['ASTM', 'ANSI', 'UL', 'NEMA', 'IEEE', 'NFPA', 'IEC', 'ISO'],
    },
    version: 1,
    status: 'published',
    lastModified: '',
  },
];

/** Resolve a SmartBlock by id from the firm catalog + built-ins. */
export function findSmartBlockInCatalog(
  id: string | undefined,
  firmCatalog: SmartBlock[]
): SmartBlock | undefined {
  if (!id) return undefined;
  if (isBuiltInSmartBlockId(id)) {
    return BUILT_IN_SMART_BLOCKS.find((sb) => sb.id === id);
  }
  return firmCatalog.find((sb) => sb.id === id);
}
